import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import MetricCard from './MetricCard';
import { TENANT_ID } from '../utils/api';
import { getApiBaseUrl } from '@/config/env';
import { Zap, Sliders, ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const PRESET_CONCURRENCIES = [10, 20, 30, 40, 50, 75, 100];

const QueueDetails: React.FC = () => {
  const { queueName } = useParams<{ queueName: string }>();
  const [queueData, setQueueData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentConcurrency, setCurrentConcurrency] = useState<number>(10);
  const [targetConcurrency, setTargetConcurrency] = useState<number>(10);
  const [concurrencySource, setConcurrencySource] = useState<'redis' | 'env'>('env');
  const [savingConcurrency, setSavingConcurrency] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { token } = useAuth();
  const { addToast } = useToast();

  const isIncomingQueue = queueName === 'incoming-messages';

  useEffect(() => {
    if (queueName) {
      fetchQueueDetails();
    }
  }, [queueName]);

  const fetchQueueDetails = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = getApiBaseUrl();
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
      };

      const response = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setQueueData(data);

        // Si es incoming-messages, inicializar concurrencia
        if (data.concurrencyInfo?.concurrency) {
          setCurrentConcurrency(data.concurrencyInfo.concurrency);
          setTargetConcurrency(data.concurrencyInfo.concurrency);
          setConcurrencySource(data.concurrencyInfo.source || 'redis');
        } else if (data.concurrency) {
          setCurrentConcurrency(data.concurrency);
          setTargetConcurrency(data.concurrency);
        }
      }
    } catch (error) {
      console.error('Error fetching queue details:', error);
      addToast({
        type: 'error',
        title: 'Error de carga',
        message: 'No se pudieron obtener los detalles de la cola',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustConcurrency = (delta: number) => {
    setTargetConcurrency((prev) => {
      const next = Math.max(1, Math.min(100, prev + delta));
      return next;
    });
  };

  const handleSetPreset = (val: number) => {
    setTargetConcurrency(val);
  };

  const handleSaveConcurrency = async () => {
    if (targetConcurrency < 1 || targetConcurrency > 100) {
      addToast({
        type: 'warning',
        title: 'Valor inválido',
        message: 'La concurrencia debe ser un número entre 1 y 100',
      });
      return;
    }

    setSavingConcurrency(true);
    setSaveSuccess(false);

    try {
      const API_BASE_URL = getApiBaseUrl();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
      };

      const response = await fetch(`${API_BASE_URL}/admin/monitoring/queues/${queueName}/concurrency`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ concurrency: targetConcurrency }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setCurrentConcurrency(result.concurrency);
        setConcurrencySource('redis');
        setSaveSuccess(true);
        addToast({
          type: 'success',
          title: 'Concurrencia actualizada',
          message: `Worker actualizado inmediatamente a ${result.concurrency} jobs en paralelo.`,
        });
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        throw new Error(result.error || 'Error al guardar la concurrencia');
      }
    } catch (err: any) {
      console.error('Error saving concurrency:', err);
      addToast({
        type: 'error',
        title: 'Fallo al actualizar',
        message: err.message || 'No se pudo aplicar la nueva concurrencia en el worker.',
      });
    } finally {
      setSavingConcurrency(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando detalles de la cola...</p>
      </div>
    );
  }

  if (!queueData) {
    return (
      <div className="error-container">
        <h2>❌ Cola no encontrada</h2>
        <p>La cola "{queueName}" no existe o no está disponible.</p>
        <Link to="/admin" className="refresh-button" style={{ marginTop: '1rem', display: 'inline-flex', textDecoration: 'none' }}>
          Volver al Overview
        </Link>
      </div>
    );
  }

  const activeJobs = queueData.active ?? queueData.metrics?.active ?? 0;
  const waitingJobs = queueData.waiting ?? queueData.metrics?.waiting ?? 0;
  const completedJobs = queueData.completed ?? queueData.metrics?.completed ?? 0;
  const failedJobs = queueData.failed ?? queueData.metrics?.failed ?? 0;
  const status = queueData.status || (failedJobs > 50 ? 'degraded' : 'active');
  const throughput = queueData.throughputPerMinute ?? queueData.metrics?.throughput ?? 0;
  const latency = queueData.averageLatency ?? queueData.metrics?.avgProcessingTime ?? 0;

  const hasConcurrencyChanges = targetConcurrency !== currentConcurrency;

  return (
    <div className="queue-details" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link
            to="/admin"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#f1f5f9',
              color: 'var(--text-secondary)',
              textDecoration: 'none',
              transition: 'background 0.2s',
            }}
            title="Volver al panel principal"
          >
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔧 {queueName}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Monitoreo y afinación de rendimiento de la cola BullMQ
            </p>
          </div>
        </div>

        <button onClick={fetchQueueDetails} className="refresh-button" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} /> Actualizar
        </button>
      </div>

      {/* METRICS CARDS */}
      <div className="metrics-grid">
        <MetricCard
          title="Jobs Activos"
          value={activeJobs}
          icon="⚡"
          type="default"
        />
        <MetricCard
          title="Jobs Esperando"
          value={waitingJobs}
          icon="⏳"
          type={waitingJobs > 20 ? 'warning' : 'default'}
        />
        <MetricCard
          title="Jobs Completados"
          value={completedJobs}
          icon="✅"
          type="success"
        />
        <MetricCard
          title="Jobs Fallidos"
          value={failedJobs}
          icon="❌"
          type={failedJobs > 0 ? 'error' : 'default'}
        />
      </div>

      {/* PANEL DE CONCURRENCIA DINÁMICA (Especial para incoming-messages o colas con soporte) */}
      {isIncomingQueue && (
        <div
          className="concurrency-control-card"
          style={{
            background: 'var(--card-background)',
            border: '1px solid #bfdbfe',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.08), 0 2px 4px -2px rgba(37, 99, 235, 0.08)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa)',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={22} color="#2563eb" />
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text-primary)' }}>
                  Afinación de Concurrencia Dinámica (INCOMING_QUEUE_CONCURRENCY)
                </h3>
                <span
                  style={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    background: concurrencySource === 'redis' ? '#dcfce7' : '#e0e7ff',
                    color: concurrencySource === 'redis' ? '#166534' : '#3730a3',
                  }}
                >
                  {concurrencySource === 'redis' ? '⚡ Redis Sincronizado' : '📄 .env Inicial'}
                </span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '0.88rem', color: 'var(--text-secondary)', maxWidth: '750px' }}>
                Controla la cantidad de mensajes entrantes de WhatsApp y webhooks que los workers de BullMQ procesan simultáneamente.
                Al guardar, la nueva concurrencia se transmite en vivo a través de <strong>Redis Pub/Sub</strong> a los procesos de Node.js sin reiniciar el servidor.
              </p>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Concurrencia Activa
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: '#2563eb', lineHeight: 1.1 }}>
                {currentConcurrency} <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>jobs / worker</span>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              padding: '1.25rem',
              background: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sliders size={18} color="var(--text-secondary)" />
                <span style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Ajustar Capacidad Simultánea:
                </span>
              </div>

              {/* CONTROLES STEPPER */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => handleAdjustConcurrency(-10)}
                  disabled={targetConcurrency <= 1 || savingConcurrency}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontWeight: 600,
                    cursor: targetConcurrency <= 1 ? 'not-allowed' : 'pointer',
                    opacity: targetConcurrency <= 1 ? 0.5 : 1,
                  }}
                  title="Restar 10"
                >
                  -10
                </button>
                <button
                  type="button"
                  onClick={() => handleAdjustConcurrency(-1)}
                  disabled={targetConcurrency <= 1 || savingConcurrency}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontWeight: 700,
                    cursor: targetConcurrency <= 1 ? 'not-allowed' : 'pointer',
                    opacity: targetConcurrency <= 1 ? 0.5 : 1,
                  }}
                  title="Restar 1"
                >
                  -1
                </button>

                <input
                  type="number"
                  min={1}
                  max={100}
                  value={targetConcurrency}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    if (Number.isInteger(parsed)) {
                      setTargetConcurrency(Math.max(1, Math.min(100, parsed)));
                    } else if (e.target.value === '') {
                      setTargetConcurrency(1);
                    }
                  }}
                  disabled={savingConcurrency}
                  style={{
                    width: '80px',
                    padding: '6px 10px',
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: hasConcurrencyChanges ? '#2563eb' : 'var(--text-primary)',
                    borderRadius: '6px',
                    border: hasConcurrencyChanges ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: '#fff',
                  }}
                />

                <button
                  type="button"
                  onClick={() => handleAdjustConcurrency(1)}
                  disabled={targetConcurrency >= 100 || savingConcurrency}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontWeight: 700,
                    cursor: targetConcurrency >= 100 ? 'not-allowed' : 'pointer',
                    opacity: targetConcurrency >= 100 ? 0.5 : 1,
                  }}
                  title="Sumar 1"
                >
                  +1
                </button>
                <button
                  type="button"
                  onClick={() => handleAdjustConcurrency(10)}
                  disabled={targetConcurrency >= 100 || savingConcurrency}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontWeight: 600,
                    cursor: targetConcurrency >= 100 ? 'not-allowed' : 'pointer',
                    opacity: targetConcurrency >= 100 ? 0.5 : 1,
                  }}
                  title="Sumar 10"
                >
                  +10
                </button>
              </div>

              {/* BOTÓN GUARDAR */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleSaveConcurrency}
                  disabled={savingConcurrency || !hasConcurrencyChanges}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: hasConcurrencyChanges ? '#2563eb' : '#94a3b8',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.92rem',
                    cursor: hasConcurrencyChanges && !savingConcurrency ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    boxShadow: hasConcurrencyChanges ? '0 2px 4px rgba(37,99,235,0.25)' : 'none',
                  }}
                >
                  {savingConcurrency ? (
                    <>
                      <div className="spinner-small" style={{ borderTopColor: '#fff' }} />
                      Aplicando a Workers...
                    </>
                  ) : saveSuccess ? (
                    <>
                      <CheckCircle2 size={16} /> ¡Aplicado en Caliente!
                    </>
                  ) : (
                    <>
                      <Zap size={16} /> Guardar Concurrencia ({targetConcurrency})
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* PRESETS RÁPIDOS */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', paddingTop: '4px' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Valores rápidos:</span>
              {PRESET_CONCURRENCIES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handleSetPreset(preset)}
                  disabled={savingConcurrency}
                  style={{
                    padding: '3px 10px',
                    borderRadius: '14px',
                    border: targetConcurrency === preset ? '1px solid #2563eb' : '1px solid #e2e8f0',
                    background: targetConcurrency === preset ? '#eff6ff' : '#fff',
                    color: targetConcurrency === preset ? '#2563eb' : 'var(--text-secondary)',
                    fontWeight: targetConcurrency === preset ? 700 : 500,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {preset}
                </button>
              ))}
            </div>

            {hasConcurrencyChanges && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#b45309' }}>
                <AlertTriangle size={15} />
                <span>Tienes cambios pendientes: pasar de <strong>{currentConcurrency}</strong> a <strong>{targetConcurrency}</strong>. Haz clic en "Guardar Concurrencia" para sincronizar los workers.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* INFORMACIÓN DE LA COLA */}
      <div className="queue-info">
        <h3>📊 Información de la Cola</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Estado:</label>
            <span className={`status ${status}`}>
              {status === 'active' ? '🟢 Activa' : '🟡 Degradada / Requiere Atención'}
            </span>
          </div>
          <div className="info-item">
            <label>Workers / Concurrencia:</label>
            <span>
              <strong>{isIncomingQueue ? currentConcurrency : (queueData.workers || 0)}</strong> jobs simultáneos
            </span>
          </div>
          <div className="info-item">
            <label>Throughput estimado:</label>
            <span>{throughput} jobs/min</span>
          </div>
          <div className="info-item">
            <label>Latencia promedio:</label>
            <span>{latency}ms</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueueDetails;
